import {
  Check,
  Copy,
  Cpu,
  KeyRound,
  Layers,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import {
  useAppConfig,
  useApplyPricingProposal,
  useCreateServiceToken,
  useCurrentPlanVersion,
  useDismissPricingProposal,
  useModelCatalog,
  usePricingProposals,
  useRevokeServiceToken,
  useRunDriftCheck,
  useSavePlanVersion,
  useServiceTokens,
  useSyncPlansToStripe,
  useUpdateAppConfig,
} from "@/api/hooks";
import type {
  AppConfigResponse,
  ModelCatalogResponse,
  PlanTierDraft,
  PlanTierResponse,
  PlanVersionResponse,
  PricingProposalResponse,
  ServiceTokenResponse,
  StripeMode,
  UpdateAppConfigRequest,
} from "@/api/types";

/** Platform settings — pencilout_admin only. v1 covers:
 *
 *  - Stripe live/test mode toggle
 *  - Per-mode API key + webhook secret (write-only inputs;
 *    last-4 of saved keys is the only thing that round-trips)
 *  - Per-mode platform price ID (recurring subscription product)
 *
 *  Plaintext credentials are encrypted server-side with Fernet
 *  (PENCILOUT_ENCRYPTION_KEY) and never returned in responses.
 */
export function SettingsPage() {
  const cfgQuery = useAppConfig();
  const update = useUpdateAppConfig();

  if (cfgQuery.isLoading && !cfgQuery.data) {
    return <p className="text-sm text-graphite-soft">Loading settings…</p>;
  }
  if (cfgQuery.error || !cfgQuery.data) {
    return (
      <p className="text-sm text-red-pencil">
        {cfgQuery.error instanceof ApiError ? cfgQuery.error.detail : "Could not load settings."}
      </p>
    );
  }
  const cfg = cfgQuery.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Settings</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Platform-level configuration. Stripe credentials are encrypted at rest with Fernet
          (per-deploy key) and never round-trip in responses — only the last 4 characters of saved
          API keys are visible here.
        </p>
      </div>

      <ModeToggleCard cfg={cfg} onSave={(mode) => update.mutate({ stripe_mode: mode })} />

      <CredentialCard
        title="Stripe — Test (sandbox)"
        mode="test"
        keyPrefix="sk_test_"
        webhookConfigured={cfg.has_test_webhook_secret}
        keyLast4={cfg.stripe_test_api_key_last4}
        priceId={cfg.stripe_platform_price_id_test}
        onSave={(body) => update.mutate(body)}
        saving={update.isPending}
      />

      <CredentialCard
        title="Stripe — Live (production)"
        mode="live"
        keyPrefix="sk_live_"
        webhookConfigured={cfg.has_live_webhook_secret}
        keyLast4={cfg.stripe_live_api_key_last4}
        priceId={cfg.stripe_platform_price_id_live}
        onSave={(body) => update.mutate(body)}
        saving={update.isPending}
      />

      <AIRoutingCard cfg={cfg} saving={update.isPending} onSave={(body) => update.mutate(body)} />

      <PlanCard activeStripeMode={cfg.stripe_mode} />

      <PricingDriftCard />

      <ServiceTokensCard />

      {update.isError && update.error && (
        <p className="text-sm text-red-pencil">
          {update.error instanceof ApiError ? update.error.detail : "Save failed."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------

function ModeToggleCard({
  cfg,
  onSave,
}: {
  cfg: ReturnType<typeof useAppConfig>["data"];
  onSave: (mode: StripeMode) => void;
}) {
  if (!cfg) return null;
  const isTest = cfg.stripe_mode === "test";
  const otherMode: StripeMode = isTest ? "live" : "test";
  const otherIsConfigured = otherMode === "live" ? cfg.has_live_api_key : cfg.has_test_api_key;

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-start gap-4">
        <SettingsIcon className="h-6 w-6 flex-none text-graphite-soft" aria-hidden />
        <div className="flex-1">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Active Stripe mode
          </h2>
          <p className="mt-2 font-mono text-2xl font-bold text-graphite">
            {isTest ? "TEST (sandbox)" : "LIVE (production)"}
          </p>
          <p className="mt-2 text-sm text-graphite-soft">
            {isTest
              ? "All Stripe calls hit your sandbox account. Charges aren't real."
              : "All Stripe calls hit your live account. Customers are charged for real."}
          </p>
        </div>
        <button
          type="button"
          disabled={!otherIsConfigured}
          onClick={() => onSave(otherMode)}
          className="rounded border border-rule bg-paper px-3 py-1.5 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
          title={
            otherIsConfigured
              ? `Switch to ${otherMode}`
              : `${otherMode} mode key isn't configured yet`
          }
        >
          Switch to {otherMode}
        </button>
      </div>
    </div>
  );
}

function CredentialCard({
  title,
  mode,
  keyPrefix,
  webhookConfigured,
  keyLast4,
  priceId,
  onSave,
  saving,
}: {
  title: string;
  mode: "test" | "live";
  keyPrefix: string;
  webhookConfigured: boolean;
  keyLast4: string | null;
  priceId: string | null;
  onSave: (body: UpdateAppConfigRequest) => void;
  saving: boolean;
}) {
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [webhookInput, setWebhookInput] = useState("");
  const [priceInput, setPriceInput] = useState(priceId ?? "");

  function commitApiKey() {
    if (!apiKeyInput.trim()) return;
    const body: UpdateAppConfigRequest =
      mode === "test"
        ? { stripe_test_api_key: apiKeyInput.trim() }
        : { stripe_live_api_key: apiKeyInput.trim() };
    onSave(body);
    setApiKeyInput("");
  }
  function commitWebhook() {
    if (!webhookInput.trim()) return;
    const body: UpdateAppConfigRequest =
      mode === "test"
        ? { stripe_test_webhook_secret: webhookInput.trim() }
        : { stripe_live_webhook_secret: webhookInput.trim() };
    onSave(body);
    setWebhookInput("");
  }
  function commitPriceId() {
    if (priceInput === (priceId ?? "")) return;
    const body: UpdateAppConfigRequest =
      mode === "test"
        ? { stripe_platform_price_id_test: priceInput.trim() }
        : { stripe_platform_price_id_live: priceInput.trim() };
    onSave(body);
  }

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-graphite-soft" aria-hidden />
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          {title}
        </h2>
      </div>

      <div className="mt-4 space-y-5">
        {/* API key */}
        <div>
          <div className="mb-1 flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wider text-graphite-soft">
              API key
            </span>
            {keyLast4 ? (
              <span className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft">
                <Check className="h-3 w-3 text-green-700" aria-hidden />
                {keyPrefix}•••• {keyLast4}
              </span>
            ) : (
              <span className="font-mono text-xs text-graphite-faint">not set</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="password"
              autoComplete="off"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder={`Paste ${keyPrefix}…`}
              className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
            />
            <button
              type="button"
              onClick={commitApiKey}
              disabled={saving || !apiKeyInput.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>

        {/* Webhook secret */}
        <div>
          <div className="mb-1 flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wider text-graphite-soft">
              Webhook secret
            </span>
            {webhookConfigured ? (
              <span className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft">
                <Check className="h-3 w-3 text-green-700" aria-hidden /> set
              </span>
            ) : (
              <span className="font-mono text-xs text-graphite-faint">not set</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="password"
              autoComplete="off"
              value={webhookInput}
              onChange={(e) => setWebhookInput(e.target.value)}
              placeholder="Paste whsec_…"
              className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
            />
            <button
              type="button"
              onClick={commitWebhook}
              disabled={saving || !webhookInput.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
            >
              Save
            </button>
          </div>
          <p className="mt-2 text-xs text-graphite-soft">
            Configure your Stripe webhook endpoint at{" "}
            <code className="rounded bg-paper px-1 font-mono">
              https://api.pencilout.app/webhooks/stripe
            </code>
            . Subscribe to <code>invoice.*</code> + <code>customer.*</code> events.
          </p>
        </div>

        {/* Platform price ID */}
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wider text-graphite-soft">
            Platform subscription Price ID
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              placeholder="price_…"
              className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
            />
            <button
              type="button"
              onClick={commitPriceId}
              disabled={saving || priceInput === (priceId ?? "")}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
            >
              Save
            </button>
          </div>
          <p className="mt-2 text-xs text-graphite-soft">
            Used when activating <code>platform_plus_usage</code> orgs. Optional — until set, only
            AI-usage invoicing works.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// AI provider routing — three model dropdowns + Anthropic API key.
// Surface for picking which provider/model handles each AI call type.
// ---------------------------------------------------------------

const _OP_LABELS: Record<string, { title: string; help: string }> = {
  walkthrough_transcribe_model: {
    title: "Walkthrough transcription",
    help: "Video → transcript + scope summary. Effectively Gemini-only — Anthropic + OpenAI don't ingest video.",
  },
  quote_generate_model: {
    title: "Quote generation",
    help: "Scope summary → draft line items. Recommended: Claude Sonnet 4.5 — its instruction-following is meaningfully better at the 'never invent scope' rule. Costs ~5× more than Gemini Flash but absolute cost is still tiny per quote.",
  },
  quote_suggest_model: {
    title: "Quote suggestions",
    help: "Find scope items mentioned in the walkthrough that aren't in the draft yet. Lower stakes than generation — Gemini Flash or Claude Haiku is fine.",
  },
};

function AIRoutingCard({
  cfg,
  saving,
  onSave,
}: {
  cfg: AppConfigResponse;
  saving: boolean;
  onSave: (body: UpdateAppConfigRequest) => void;
}) {
  const catalogQuery = useModelCatalog();
  const [anthropicInput, setAnthropicInput] = useState("");

  function commitAnthropicKey() {
    if (!anthropicInput.trim()) return;
    onSave({ anthropic_api_key: anthropicInput.trim() });
    setAnthropicInput("");
  }

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-center gap-2">
        <Cpu className="h-4 w-4 text-graphite-soft" aria-hidden />
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          AI provider routing
        </h2>
      </div>
      {catalogQuery.data && (
        <p className="mt-2 font-mono text-[11px] text-graphite-soft">
          Pricing version <strong>{catalogQuery.data.pricing_version}</strong>, last verified{" "}
          {catalogQuery.data.last_verified_on}.
        </p>
      )}

      <div className="mt-4 space-y-5">
        {/* ---- Anthropic API key ---- */}
        <div>
          <div className="mb-1 flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wider text-graphite-soft">
              Anthropic API key
            </span>
            {cfg.has_anthropic_api_key ? (
              <span className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft">
                <Check className="h-3 w-3 text-green-700" aria-hidden />
                sk-ant-•••• {cfg.anthropic_api_key_last4 ?? "????"}
              </span>
            ) : (
              <span className="font-mono text-xs text-graphite-faint">not set</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="password"
              autoComplete="off"
              value={anthropicInput}
              onChange={(e) => setAnthropicInput(e.target.value)}
              placeholder="Paste sk-ant-…"
              className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
            />
            <button
              type="button"
              onClick={commitAnthropicKey}
              disabled={saving || !anthropicInput.trim()}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
            >
              Save
            </button>
          </div>
          <p className="mt-2 text-xs text-graphite-soft">
            Required for routing any operation to Claude. Get one at{" "}
            <a
              href="https://console.anthropic.com/settings/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              console.anthropic.com/settings/keys
            </a>
            .
          </p>
        </div>

        {/* ---- Model dropdowns ---- */}
        {(
          ["walkthrough_transcribe_model", "quote_generate_model", "quote_suggest_model"] as const
        ).map((field) => (
          <ModelSelectRow
            key={field}
            field={field}
            currentKey={cfg[field]}
            catalog={catalogQuery.data}
            hasAnthropic={cfg.has_anthropic_api_key}
            saving={saving}
            onSave={(value) => onSave({ [field]: value })}
          />
        ))}
      </div>
    </div>
  );
}

function ModelSelectRow({
  field,
  currentKey,
  catalog,
  hasAnthropic,
  saving,
  onSave,
}: {
  field: keyof typeof _OP_LABELS;
  currentKey: string | null;
  catalog: ModelCatalogResponse | undefined;
  hasAnthropic: boolean;
  saving: boolean;
  onSave: (value: string) => void;
}) {
  const labels = _OP_LABELS[field] ?? { title: field, help: "" };
  const [draft, setDraft] = useState<string>(currentKey ?? "");

  // Keep the draft in sync when the upstream config changes
  // (e.g. another tab saved a different value).
  if (currentKey !== null && draft === "" && currentKey !== draft) {
    setDraft(currentKey);
  }

  const dirty = draft !== (currentKey ?? "");

  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-graphite-soft">
        {labels.title}
      </div>
      <div className="flex gap-2">
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
        >
          <option value="">(use code default)</option>
          {catalog &&
            Object.entries(catalog.by_provider).map(([provider, entries]) => (
              <optgroup key={provider} label={provider.replace(/_/g, " ")}>
                {entries.map((entry) => {
                  const disabled = entry.key.startsWith("anthropic/") && !hasAnthropic;
                  return (
                    <option key={entry.key} value={entry.key} disabled={disabled}>
                      {entry.key} — ${entry.input_dollars_per_1m.toFixed(2)}/M in · $
                      {entry.output_dollars_per_1m.toFixed(2)}/M out
                      {disabled ? " (set Anthropic key first)" : ""}
                    </option>
                  );
                })}
              </optgroup>
            ))}
        </select>
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={saving || !dirty}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          Save
        </button>
      </div>
      <p className="mt-2 text-xs text-graphite-soft">{labels.help}</p>
    </div>
  );
}

// ---------------------------------------------------------------
// Plans — customer-facing pricing tiers
// ---------------------------------------------------------------

/** Convert a tier response into the editable draft shape. */
function toDraft(tier: PlanTierResponse): PlanTierDraft {
  return {
    tier: tier.tier,
    name: tier.name,
    tagline: tier.tagline,
    price_monthly_cents: tier.price_monthly_cents,
    included_walkthroughs: tier.included_walkthroughs,
    overage_cents_per_walkthrough: tier.overage_cents_per_walkthrough,
    watermark_pdf: tier.watermark_pdf,
    is_public: tier.is_public,
    sort_order: tier.sort_order,
    features: [...tier.features],
  };
}

function draftsEqual(a: PlanTierDraft[], b: PlanTierDraft[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function PlanCard({ activeStripeMode }: { activeStripeMode: StripeMode }) {
  const versionQuery = useCurrentPlanVersion();
  const save = useSavePlanVersion();
  const sync = useSyncPlansToStripe();

  const serverDrafts: PlanTierDraft[] = useMemo(
    () => (versionQuery.data?.tiers ?? []).map(toDraft),
    [versionQuery.data],
  );
  const [drafts, setDrafts] = useState<PlanTierDraft[]>(serverDrafts);

  // Keep local drafts in sync when the server data refreshes (e.g.
  // after a successful save or a tab refocus).
  useEffect(() => {
    setDrafts(serverDrafts);
  }, [serverDrafts]);

  const dirty = !draftsEqual(serverDrafts, drafts);

  function patchTier(idx: number, patch: Partial<PlanTierDraft>) {
    setDrafts((current) => current.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  async function commitSave() {
    await save.mutateAsync({
      note: `Edited via admin Settings (${new Date().toISOString()})`,
      tiers: drafts,
    });
  }

  async function commitSync() {
    await sync.mutateAsync();
  }

  if (versionQuery.isLoading && !versionQuery.data) {
    return (
      <div className="rounded-lg border border-rule bg-paper-warm p-5">
        <p className="text-sm text-graphite-soft">Loading plans…</p>
      </div>
    );
  }
  if (versionQuery.error) {
    return (
      <div className="rounded-lg border border-rule bg-paper-warm p-5">
        <p className="text-sm text-red-pencil">
          {versionQuery.error instanceof ApiError
            ? versionQuery.error.detail
            : "Could not load plan version."}
        </p>
      </div>
    );
  }
  if (!versionQuery.data) {
    return null;
  }
  const version = versionQuery.data;

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-graphite-soft" aria-hidden />
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Customer-facing plans
          </h2>
        </div>
        <PlanVersionMeta version={version} />
      </div>

      <p className="mt-2 max-w-3xl text-xs text-graphite-soft">
        Tier definitions sent to Stripe on sync. Saving creates a fresh append-only version row.
        Stripe Prices are immutable — bumping a price always mints a new Price ID on the next sync.
      </p>

      <div className="mt-4 space-y-3">
        {drafts.map((draft, idx) => {
          const serverTier = version.tiers.find((t) => t.tier === draft.tier);
          if (!serverTier) return null;
          return (
            <PlanTierEditor
              key={draft.tier}
              draft={draft}
              serverTier={serverTier}
              activeStripeMode={activeStripeMode}
              onChange={(patch) => patchTier(idx, patch)}
            />
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-rule pt-4">
        <button
          type="button"
          onClick={() => void commitSave()}
          disabled={!dirty || save.isPending}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save new version"}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setDrafts(serverDrafts)}
            disabled={save.isPending}
            className="rounded border border-rule px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:border-graphite hover:text-graphite disabled:opacity-40"
          >
            Discard edits
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => void commitSync()}
            disabled={dirty || sync.isPending}
            className="inline-flex items-center gap-2 rounded border border-graphite bg-paper px-3 py-1.5 font-mono text-sm font-semibold text-graphite transition hover:bg-graphite hover:text-paper disabled:opacity-40"
            title={
              dirty
                ? "Save edits first, then sync"
                : `Push current tiers to Stripe (${activeStripeMode} mode)`
            }
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${sync.isPending ? "animate-spin" : ""}`}
              aria-hidden
            />
            {sync.isPending ? "Syncing…" : `Sync to Stripe (${activeStripeMode})`}
          </button>
        </div>
      </div>

      {save.isError && save.error && (
        <p className="mt-3 text-sm text-red-pencil">
          {save.error instanceof ApiError ? save.error.detail : "Save failed."}
        </p>
      )}
      {sync.isError && sync.error && (
        <p className="mt-3 text-sm text-red-pencil">
          {sync.error instanceof ApiError ? sync.error.detail : "Sync failed."}
        </p>
      )}
      {sync.isSuccess && sync.data && <SyncSuccessSummary data={sync.data} />}
    </div>
  );
}

function PlanVersionMeta({ version }: { version: PlanVersionResponse }) {
  return (
    <p className="font-mono text-[11px] text-graphite-soft">
      v <strong>{version.version}</strong>
      {" · "}
      effective {new Date(version.effective_from).toLocaleDateString()}
    </p>
  );
}

function PlanTierEditor({
  draft,
  serverTier,
  activeStripeMode,
  onChange,
}: {
  draft: PlanTierDraft;
  serverTier: PlanTierResponse;
  activeStripeMode: StripeMode;
  onChange: (patch: Partial<PlanTierDraft>) => void;
}) {
  const isFreeTier =
    draft.price_monthly_cents === 0 && draft.overage_cents_per_walkthrough === null;
  const synced =
    activeStripeMode === "test" ? serverTier.is_synced_test : serverTier.is_synced_live;
  const otherSynced =
    activeStripeMode === "test" ? serverTier.is_synced_live : serverTier.is_synced_test;

  return (
    <div className="rounded border border-rule bg-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h3 className="font-mono text-sm font-bold tracking-tight">{draft.name}</h3>
          <span className="font-mono text-[11px] uppercase tracking-wider text-graphite-faint">
            {draft.tier}
          </span>
        </div>
        <SyncBadge
          synced={synced}
          otherSynced={otherSynced}
          mode={activeStripeMode}
          isFreeTier={isFreeTier}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Display name" value={draft.name} onChange={(v) => onChange({ name: v })} />
        <Field
          label="Tagline"
          value={draft.tagline}
          onChange={(v) => onChange({ tagline: v })}
          placeholder="e.g. For active remodelers"
        />
        <CentsField
          label="Monthly price ($)"
          cents={draft.price_monthly_cents}
          onChange={(cents) => onChange({ price_monthly_cents: cents ?? 0 })}
          help="$0 = free tier"
        />
        <NumberField
          label="Included quotes / month"
          value={draft.included_walkthroughs}
          onChange={(v) => onChange({ included_walkthroughs: v })}
          min={0}
        />
        <CentsField
          label="Overage rate ($/quote)"
          cents={draft.overage_cents_per_walkthrough ?? null}
          onChange={(cents) => onChange({ overage_cents_per_walkthrough: cents })}
          allowNull
          help="Blank = no overage allowed"
        />
        <NumberField
          label="Sort order"
          value={draft.sort_order}
          onChange={(v) => onChange({ sort_order: v })}
          min={0}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
        <label className="inline-flex items-center gap-2 text-graphite-soft">
          <input
            type="checkbox"
            checked={draft.is_public}
            onChange={(e) => onChange({ is_public: e.target.checked })}
            className="h-3.5 w-3.5"
          />
          Public on pricing page
        </label>
        <label className="inline-flex items-center gap-2 text-graphite-soft">
          <input
            type="checkbox"
            checked={draft.watermark_pdf}
            onChange={(e) => onChange({ watermark_pdf: e.target.checked })}
            className="h-3.5 w-3.5"
          />
          Watermark PDFs
        </label>
      </div>

      {draft.features.length > 0 && (
        <div className="mt-3">
          <p className="mb-1 text-[11px] uppercase tracking-wider text-graphite-faint">Features</p>
          <ul className="list-disc pl-5 text-xs text-graphite-soft">
            {draft.features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
          <p className="mt-1 text-[11px] text-graphite-faint">
            Edit feature copy via the API for now — UI editor coming.
          </p>
        </div>
      )}

      {!isFreeTier && (
        <div className="mt-3 grid gap-1 font-mono text-[11px] text-graphite-faint sm:grid-cols-2">
          <div>test product: {serverTier.stripe_test_product_id ?? "—"}</div>
          <div>live product: {serverTier.stripe_live_product_id ?? "—"}</div>
          <div>test monthly: {serverTier.stripe_test_price_id_monthly ?? "—"}</div>
          <div>live monthly: {serverTier.stripe_live_price_id_monthly ?? "—"}</div>
          {(draft.overage_cents_per_walkthrough ?? 0) > 0 && (
            <>
              <div>test overage: {serverTier.stripe_test_price_id_overage ?? "—"}</div>
              <div>live overage: {serverTier.stripe_live_price_id_overage ?? "—"}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SyncBadge({
  synced,
  otherSynced,
  mode,
  isFreeTier,
}: {
  synced: boolean;
  otherSynced: boolean;
  mode: StripeMode;
  isFreeTier: boolean;
}) {
  if (isFreeTier) {
    return (
      <span className="rounded bg-paper px-2 py-0.5 font-mono text-[11px] text-graphite-soft">
        no Stripe needed
      </span>
    );
  }
  if (synced) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-paper px-2 py-0.5 font-mono text-[11px] text-graphite-soft">
        <Check className="h-3 w-3 text-green-700" aria-hidden />
        synced ({mode})
      </span>
    );
  }
  return (
    <span className="rounded bg-paper px-2 py-0.5 font-mono text-[11px] text-red-pencil">
      not synced ({mode}){otherSynced ? " · other mode synced" : ""}
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-[11px] uppercase tracking-wider text-graphite-soft">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-[11px] uppercase tracking-wider text-graphite-soft">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
      />
    </label>
  );
}

function CentsField({
  label,
  cents,
  onChange,
  allowNull,
  help,
}: {
  label: string;
  cents: number | null;
  onChange: (cents: number | null) => void;
  allowNull?: boolean;
  help?: string;
}) {
  // Show dollars in the input. Empty string + allowNull → null.
  const displayValue = cents === null ? "" : (cents / 100).toFixed(2);
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-[11px] uppercase tracking-wider text-graphite-soft">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === "") {
            onChange(allowNull ? null : 0);
            return;
          }
          const dollars = Number(raw);
          if (Number.isFinite(dollars) && dollars >= 0) {
            onChange(Math.round(dollars * 100));
          }
        }}
        className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
      />
      {help && <span className="text-[11px] text-graphite-faint">{help}</span>}
    </label>
  );
}

function SyncSuccessSummary({
  data,
}: {
  data: {
    new_version: string;
    mode: StripeMode;
    tiers: Array<{ tier: string; actions: string[] }>;
  };
}) {
  return (
    <div className="mt-3 rounded border border-rule bg-paper p-3 font-mono text-[11px] text-graphite-soft">
      <p className="mb-1">
        Synced to Stripe <strong>{data.mode}</strong> · new version{" "}
        <strong>{data.new_version}</strong>
      </p>
      <ul className="list-disc pl-5">
        {data.tiers.map((t) => (
          <li key={t.tier}>
            <strong>{t.tier}</strong>: {t.actions.join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------
// Service tokens — long-lived API auth for cron + scripts
// ---------------------------------------------------------------

function ServiceTokensCard() {
  const tokens = useServiceTokens();
  const create = useCreateServiceToken();
  const revoke = useRevokeServiceToken();
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"cron" | "pencilout_admin">("cron");
  const [expiresInDays, setExpiresInDays] = useState<string>("");
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function commitCreate() {
    setError(null);
    if (!name.trim()) return;
    let expiresAt: string | undefined;
    if (expiresInDays.trim()) {
      const days = Number(expiresInDays);
      if (!Number.isFinite(days) || days <= 0) {
        setError("Expires-in days must be a positive number.");
        return;
      }
      const dt = new Date(Date.now() + days * 86_400_000);
      expiresAt = dt.toISOString();
    }
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        scope,
        expires_at: expiresAt,
      });
      setFreshToken(res.raw_token);
      setName("");
      setScope("cron");
      setExpiresInDays("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-graphite-soft" aria-hidden />
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Service tokens
        </h2>
      </div>
      <p className="mt-2 max-w-prose text-xs text-graphite-soft">
        Long-lived API tokens for cron jobs and scripts. Pick <strong>cron</strong> for crontab use
        — limited to the apply-pending and drift-check endpoints, so a leaked token can't
        impersonate or edit billing keys. <strong>pencilout_admin</strong> grants full admin power;
        use only when you genuinely need broad access. The raw token is shown ONCE on creation and
        can't be recovered.
      </p>

      {/* Mint form */}
      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_140px_120px_auto]">
        <input
          type="text"
          placeholder="Name (e.g. cron-apply-pending)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
        />
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as "cron" | "pencilout_admin")}
          className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          title="Scope — picks which endpoints this token can call."
        >
          <option value="cron">cron</option>
          <option value="pencilout_admin">pencilout_admin</option>
        </select>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Expires (days)"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(e.target.value)}
          className="rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
          title="Optional. Leave blank for no expiry."
        />
        <button
          type="button"
          onClick={() => void commitCreate()}
          disabled={!name.trim() || create.isPending}
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {create.isPending ? "Minting…" : "Create token"}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-pencil">{error}</p>}

      {/* Fresh-token reveal */}
      {freshToken && (
        <FreshTokenReveal rawToken={freshToken} onDismiss={() => setFreshToken(null)} />
      )}

      {/* List */}
      {tokens.isLoading ? (
        <p className="mt-4 text-xs text-graphite-soft">Loading…</p>
      ) : tokens.data && tokens.data.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {tokens.data.map((t) => (
            <ServiceTokenRow
              key={t.id}
              token={t}
              onRevoke={() => revoke.mutate(t.id)}
              revoking={revoke.isPending && revoke.variables === t.id}
            />
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-xs text-graphite-soft">No tokens yet.</p>
      )}
    </div>
  );
}

function FreshTokenReveal({
  rawToken,
  onDismiss,
}: {
  rawToken: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(rawToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="mt-4 rounded border border-pencil-yellow bg-pencil-yellow-soft p-4">
      <p className="font-mono text-xs font-bold uppercase tracking-wider text-graphite">
        Copy this token now — you won't see it again
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-paper px-2 py-1.5 font-mono text-xs">
          {rawToken}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded border border-graphite bg-paper px-2 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-graphite hover:text-paper"
        >
          <Copy className="h-3 w-3" aria-hidden />
          {copied ? "Copied!" : "Copy"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1.5 font-mono text-xs text-graphite-soft hover:text-graphite"
        >
          Dismiss
        </button>
      </div>
      <p className="mt-2 font-mono text-[11px] text-graphite-soft">
        Use as <code>Authorization: Bearer {"{token}"}</code> from cron or scripts.
      </p>
    </div>
  );
}

function ServiceTokenRow({
  token,
  onRevoke,
  revoking,
}: {
  token: ServiceTokenResponse;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const isRevoked = token.revoked_at != null;
  const isExpired = token.expires_at != null && new Date(token.expires_at) <= new Date();
  const inactive = isRevoked || isExpired;

  return (
    <li
      className={`flex flex-wrap items-center gap-3 rounded border p-3 ${
        inactive ? "border-rule bg-paper opacity-60" : "border-rule bg-paper"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <strong className="font-mono text-sm">{token.name}</strong>
          <span className="font-mono text-[11px] text-graphite-soft">
            pt_svc_•••• {token.last_4}
          </span>
          {isRevoked && (
            <span className="rounded bg-red-pencil/10 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-red-pencil">
              revoked
            </span>
          )}
          {isExpired && !isRevoked && (
            <span className="rounded bg-red-pencil/10 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-red-pencil">
              expired
            </span>
          )}
        </div>
        <div className="mt-1 font-mono text-[11px] text-graphite-soft">
          created {new Date(token.created_at).toLocaleDateString()}
          {token.expires_at && ` · expires ${new Date(token.expires_at).toLocaleDateString()}`}
          {token.last_used_at
            ? ` · last used ${new Date(token.last_used_at).toLocaleString()}`
            : " · never used"}
        </div>
      </div>
      {!inactive && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="inline-flex items-center gap-1 rounded border border-rule px-2 py-1 font-mono text-xs text-red-pencil transition hover:border-red-pencil disabled:opacity-40"
        >
          <Trash2 className="h-3 w-3" aria-hidden />
          {revoking ? "Revoking…" : "Revoke"}
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------
// Pricing drift — review proposals from the weekly cron
// ---------------------------------------------------------------

function PricingDriftCard() {
  const proposals = usePricingProposals(true);
  const runCheck = useRunDriftCheck();
  const apply = useApplyPricingProposal();
  const dismiss = useDismissPricingProposal();
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lastCheckSummary, setLastCheckSummary] = useState<string | null>(null);

  async function commitRunCheck() {
    setError(null);
    setLastCheckSummary(null);
    try {
      const res = await runCheck.mutateAsync();
      const counts = {
        unchanged: 0,
        proposal_created: 0,
        failed: 0,
        no_models: 0,
      } as Record<string, number>;
      for (const p of res.providers) {
        counts[p.status] = (counts[p.status] ?? 0) + 1;
      }
      setLastCheckSummary(
        `Checked ${res.providers.length} providers. ` +
          `${counts.proposal_created ?? 0} new proposal(s), ` +
          `${counts.unchanged ?? 0} unchanged, ` +
          `${counts.failed ?? 0} failed.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  async function commitApply(id: string) {
    setError(null);
    try {
      await apply.mutateAsync(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  async function commitDismiss(id: string) {
    setError(null);
    try {
      await dismiss.mutateAsync({ id, reason: dismissReason || null });
      setDismissingId(null);
      setDismissReason("");
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-graphite-soft" aria-hidden />
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            Pricing drift
          </h2>
        </div>
        <button
          type="button"
          onClick={() => void commitRunCheck()}
          disabled={runCheck.isPending}
          className="inline-flex items-center gap-2 rounded border border-graphite bg-paper px-3 py-1.5 font-mono text-sm font-semibold text-graphite transition hover:bg-graphite hover:text-paper disabled:opacity-40"
        >
          <Search
            className={`h-3.5 w-3.5 ${runCheck.isPending ? "animate-pulse" : ""}`}
            aria-hidden
          />
          {runCheck.isPending ? "Checking…" : "Run check now"}
        </button>
      </div>

      <p className="mt-2 max-w-prose text-xs text-graphite-soft">
        Weekly cron (Sundays 03:00 UTC) fetches each AI provider's pricing page, compares to the
        active rate book, writes a proposal here when anything changes. Apply mints a fresh
        ``PricingVersionRecord``; existing AI cost events keep their old rate label.
      </p>

      {lastCheckSummary && (
        <p className="mt-3 rounded border border-rule bg-paper p-2 font-mono text-[11px] text-graphite-soft">
          {lastCheckSummary}
        </p>
      )}

      {proposals.isLoading ? (
        <p className="mt-4 text-xs text-graphite-soft">Loading…</p>
      ) : proposals.data && proposals.data.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {proposals.data.map((p) => (
            <ProposalRow
              key={p.id}
              proposal={p}
              applying={apply.isPending && apply.variables === p.id}
              dismissing={dismissingId === p.id}
              dismissReason={dismissReason}
              onSetDismissing={setDismissingId}
              onSetDismissReason={setDismissReason}
              onApply={() => void commitApply(p.id)}
              onCommitDismiss={() => void commitDismiss(p.id)}
            />
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-xs text-graphite-soft">
          No pending proposals. The cron has been quiet — provider rates match the active version.
        </p>
      )}

      {error && <p className="mt-3 text-sm text-red-pencil">{error}</p>}
    </div>
  );
}

function ProposalRow({
  proposal,
  applying,
  dismissing,
  dismissReason,
  onSetDismissing,
  onSetDismissReason,
  onApply,
  onCommitDismiss,
}: {
  proposal: PricingProposalResponse;
  applying: boolean;
  dismissing: boolean;
  dismissReason: string;
  onSetDismissing: (id: string | null) => void;
  onSetDismissReason: (reason: string) => void;
  onApply: () => void;
  onCommitDismiss: () => void;
}) {
  const confidenceColor =
    proposal.confidence === "high"
      ? "text-graphite"
      : proposal.confidence === "medium"
        ? "text-amber-700"
        : "text-red-pencil";

  return (
    <li className="rounded border border-rule bg-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-3">
          <strong className="font-mono text-sm uppercase tracking-wider">
            {proposal.provider}
          </strong>
          <span className={`font-mono text-[11px] uppercase tracking-wider ${confidenceColor}`}>
            {proposal.confidence} confidence
          </span>
          <span className="font-mono text-[11px] text-graphite-soft">
            v {proposal.active_pricing_version}
          </span>
        </div>
        <span className="font-mono text-[11px] text-graphite-faint">
          {new Date(proposal.detected_at).toLocaleString()}
        </span>
      </div>

      <pre className="mt-3 whitespace-pre-wrap rounded bg-paper-warm p-3 font-mono text-xs text-graphite">
        {proposal.changes_summary}
      </pre>

      {proposal.evidence && (
        <details className="mt-2">
          <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            Evidence (verbatim)
          </summary>
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-paper-warm p-3 font-mono text-[11px] text-graphite-soft">
            {proposal.evidence}
          </pre>
        </details>
      )}

      {proposal.source_url && (
        <p className="mt-2 font-mono text-[11px] text-graphite-soft">
          source:{" "}
          <a
            href={proposal.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-graphite"
          >
            {proposal.source_url}
          </a>
        </p>
      )}

      {dismissing ? (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            placeholder="Optional: reason for dismissing (e.g. 'page reformat, no real change')"
            value={dismissReason}
            onChange={(e) => onSetDismissReason(e.target.value)}
            className="w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-xs"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCommitDismiss}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
            >
              Confirm dismiss
            </button>
            <button
              type="button"
              onClick={() => {
                onSetDismissing(null);
                onSetDismissReason("");
              }}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-graphite-soft hover:border-graphite hover:text-graphite"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onApply}
            disabled={applying}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-40"
          >
            {applying ? "Applying…" : "Apply (mint new version)"}
          </button>
          <button
            type="button"
            onClick={() => onSetDismissing(proposal.id)}
            className="rounded border border-rule px-3 py-1.5 font-mono text-xs text-red-pencil hover:border-red-pencil"
          >
            Dismiss
          </button>
        </div>
      )}
    </li>
  );
}
